import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import tokenStore from '../../utils/tokenStore';
import { API_URL } from '../../constants/api';
import Wordmark from '../brand/Wordmark';
import AuthShowcasePanel from './AuthShowcasePanel';
import GoogleSignInButton from './GoogleSignInButton';
import './LoginForm.css';

const API = `${API_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_CF_TURNSTILE_SITE_KEY || "";
const GOOGLE_ENABLED = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

const RegisterForm = ({ onLogin, switchToLogin }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Load and render the Turnstile widget once the component mounts.
  // If no site key is configured (local dev), the widget is skipped entirely
  // and the backend will also skip verification.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    const scriptId = "cf-turnstile-script";
    const renderWidget = () => {
      if (turnstileRef.current && window.turnstile && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(""),
          theme: "dark",
        });
      }
    };

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else if (window.turnstile) {
      renderWidget();
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // In production, require a Turnstile token before submitting
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Please complete the bot verification check.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = { ...formData };
      if (turnstileToken) payload.cf_turnstile_token = turnstileToken;

      const response = await axios.post(`${API}/auth/register`, payload);

      if (response.data.access_token) {
        tokenStore.set(response.data.access_token);

        try {
          const userResponse = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${response.data.access_token}` }
          });
          await onLogin(userResponse.data);
        } catch (userError) {
          console.error('Failed to get user details after registration:', userError);
          await onLogin();
        }
      } else {
        setError("Registration failed - no token received");
      }
    } catch (err) {
      let errorMessage = "Registration failed";
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData) && errorData.length > 0) {
          errorMessage = errorData[0].msg || errorMessage;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-entry-shell auth-entry-shell--register" data-theme="light">
      <div className="auth-entry-grid">
        <AuthShowcasePanel isRegister />
        <div className="auth-entry-form-wrap">
          <div className="fp-login auth-entry-card">
            <div className="fp-login-brand">
              <Wordmark
                plusColor="#1A9B8E"
                style={{ height: '26px', width: 'auto', color: '#2C3E50' }}
              />
              <h1 className="fp-login-title">Join Founders+</h1>
              <p className="fp-login-subtitle">Build. Create. Connect.</p>
            </div>

            {error && (
              <div className="fp-login-error" role="alert">
                <span className="fp-login-error-icon" aria-hidden="true">!</span>
                <span>{error}</span>
              </div>
            )}

            {GOOGLE_ENABLED && (
              <>
                <GoogleSignInButton mode="signup" onSuccess={onLogin} disabled={loading} />
                <div className="fp-login-divider" aria-hidden="true">
                  <span>or sign up with email</span>
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="fp-login-form" noValidate>
              <div className="fp-login-field">
                <label htmlFor="fp-reg-email" className="fp-login-label">Email</label>
                <div className="fp-login-input-wrap">
                  <input
                    id="fp-reg-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="fp-login-input"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="fp-login-field">
                <label htmlFor="fp-reg-password" className="fp-login-label">Password</label>
                <div className="fp-login-input-wrap">
                  <input
                    id="fp-reg-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="fp-login-input fp-login-input--password"
                    disabled={loading}
                    required
                    minLength={8}
                    aria-describedby="fp-reg-password-help"
                  />
                  <button
                    type="button"
                    className="fp-login-pw-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <p
                  id="fp-reg-password-help"
                  className="fp-login-hint"
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: formData.password.length === 0
                      ? 'var(--text-muted, #888)'
                      : formData.password.length >= 8 ? '#22c55e' : '#f59e0b',
                  }}
                >
                  {formData.password.length === 0
                    ? 'Use 8+ characters. A short phrase works great.'
                    : formData.password.length >= 8
                      ? '✓ Looks good'
                      : `${8 - formData.password.length} more character${8 - formData.password.length === 1 ? '' : 's'} to go`}
                </p>
              </div>

              <button type="submit" disabled={loading || (TURNSTILE_SITE_KEY && !turnstileToken)} className="fp-login-submit">
                {loading ? (
                  <>
                    <span className="fp-login-spinner" aria-hidden="true" />
                    <span>Creating your account...</span>
                  </>
                ) : (
                  'Join the Community'
                )}
              </button>

              {/* Cloudflare Turnstile widget — only rendered when site key is configured */}
              {TURNSTILE_SITE_KEY && (
                <div
                  ref={turnstileRef}
                  style={{ marginTop: 12, display: "flex", justifyContent: "center" }}
                />
              )}
            </form>

            <div className="fp-login-footer">
              <p className="fp-login-footer-text">
                Already have an account?{' '}
                <button type="button" onClick={switchToLogin} className="fp-login-footer-cta">
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
